import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { Helmet } from 'react-helmet';
import { getImageUrl, hideBrokenImage } from '../utils/imageUrl';

const BlogDetail = () => {
  const { slug } = useParams();
  const { user } = useAuth();
  const [blog, setBlog] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchBlog = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/blogs/slug/${slug}`);
      const blog = response.data.blog || response.data.data?.blog;
      setBlog(blog);
      setRelatedPosts(blog?.relatedPosts || []);
    } catch (error) {
      toast.error('Blog not found');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchBlog();
  }, [fetchBlog]);

  const sharePost = (platform) => {
    const url = `${window.location.origin}/blog/${blog.slug}`;
    const title = blog.title;
    
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      twitter: `https://twitter.com/intent/tweet?url=${url}&text=${title}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      whatsapp: `https://wa.me/?text=${title} ${url}`
    };

    window.open(shareUrls[platform], '_blank', 'width=600,height=400');
  };

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  if (!blog) {
    return <div className="text-center py-12">Blog not found</div>;
  }

  return (
    <>
      <Helmet>
        <title>{blog.meta_title || blog.title}</title>
        <meta name="description" content={blog.meta_description} />
        <meta name="keywords" content={blog.meta_keywords} />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content={blog.title} />
        <meta property="og:description" content={blog.meta_description} />
        {blog.featured_image && <meta property="og:image" content={getImageUrl(blog.featured_image)} />}
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="article" />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={blog.title} />
        <meta name="twitter:description" content={blog.meta_description} />
        {blog.featured_image && <meta name="twitter:image" content={getImageUrl(blog.featured_image)} />}
        
        {/* Schema Markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: blog.title,
            description: blog.meta_description,
            image: getImageUrl(blog.featured_image),
            datePublished: blog.publish_date,
            dateModified: blog.updated_at,
            author: {
              '@type': 'Person',
              name: blog.author_name
            },
            publisher: {
              '@type': 'Organization',
              name: 'PRAL'
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Header Navigation */}
        <header className="bg-white shadow-sm border-b sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              <div className="flex items-center">
                <img 
                  src="/fbrlogo1.svg" 
                  alt="FBR Logo" 
                  className="h-10 w-10 mr-3"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <Link to="/" className="text-2xl font-bold text-blue-600 hover:text-blue-700">
                  FBRSTAPP
                </Link>
              </div>
              <nav className="flex items-center space-x-6">
                <Link to="/" className="text-gray-600 hover:text-gray-900 font-medium">Home</Link>
                <Link to="/blog" className="text-gray-600 hover:text-gray-900 font-medium">Blog</Link>
                {user ? (
                  <Link
                    to="/app/dashboard"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                  >
                    Dashboard
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/login"
                      className="text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Sign In
                    </Link>
                    <Link
                      to="/register"
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                    >
                      Get Started
                    </Link>
                  </>
                )}
              </nav>
            </div>
          </div>
        </header>

        {/* Breadcrumb */}
        <div className="bg-white border-b py-4">
          <div className="max-w-4xl mx-auto px-4">
            <div className="text-sm text-gray-600">
              <Link to="/blog" className="hover:text-blue-600">Blog</Link>
              {blog.category_name && (
                <>
                  <span className="mx-2">/</span>
                  <Link to={`/blog/category/${blog.category_slug}`} className="hover:text-blue-600">
                    {blog.category_name}
                  </Link>
                </>
              )}
              <span className="mx-2">/</span>
              <span>{blog.title}</span>
            </div>
          </div>
        </div>

        {/* Featured Image */}
        {blog.featured_image && (
          <img
            src={getImageUrl(blog.featured_image)}
            alt={blog.title}
            className="w-full h-96 object-cover"
            onError={hideBrokenImage}
          />
        )}

        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-4 py-12">
          <article className="bg-white rounded-lg shadow-lg p-8">
            {/* Header */}
            <header className="mb-8 border-b pb-6">
              <h1 className="text-4xl font-bold mb-4">{blog.title}</h1>
              
              <div className="flex flex-wrap gap-4 text-gray-600 mb-4">
                <div>
                  <span className="font-semibold">By</span> {blog.author_name}
                </div>
                <div>
                  <span className="font-semibold">Published</span> {new Date(blog.publish_date).toLocaleDateString()}
                </div>
                <div>
                  <span className="font-semibold">Views</span> {blog.views}
                </div>
              </div>

              {/* Tags */}
              {blog.tags && blog.tags.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {blog.tags.map(tag => (
                    <Link
                      key={tag.id}
                      to={`/blog/tag/${tag.slug}`}
                      className="inline-block px-3 py-1 bg-gray-200 text-gray-700 rounded text-sm hover:bg-gray-300"
                    >
                      #{tag.name}
                    </Link>
                  ))}
                </div>
              )}

              {/* Social Share */}
              <div className="mt-4 flex gap-3">
                <button
                  onClick={() => sharePost('facebook')}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                >
                  Share on Facebook
                </button>
                <button
                  onClick={() => sharePost('twitter')}
                  className="px-4 py-2 bg-sky-400 text-white rounded hover:bg-sky-500 text-sm"
                >
                  Share on Twitter
                </button>
                <button
                  onClick={() => sharePost('linkedin')}
                  className="px-4 py-2 bg-blue-800 text-white rounded hover:bg-blue-900 text-sm"
                >
                  Share on LinkedIn
                </button>
                <button
                  onClick={() => sharePost('whatsapp')}
                  className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
                >
                  Share on WhatsApp
                </button>
              </div>
            </header>

            {/* Content */}
            <div className="prose prose-lg max-w-none mb-8">
              <div
                className="rich-text"
                dangerouslySetInnerHTML={{ __html: blog.content }}
              />
            </div>

            {/* Footer */}
            <footer className="border-t pt-6">
              <div className="text-sm text-gray-600">
                Last updated: {new Date(blog.updated_at).toLocaleDateString()}
              </div>
            </footer>
          </article>

          {/* Related Posts */}
          {relatedPosts.length > 0 && (
            <div className="mt-12">
              <h2 className="text-3xl font-bold mb-6">Related Posts</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedPosts.slice(0, 3).map(post => (
                  <Link
                    key={post.id}
                    to={`/blog/${post.slug}`}
                    className="bg-white rounded-lg shadow hover:shadow-lg transition"
                  >
                    {post.featured_image && (
                      <img
                        src={getImageUrl(post.featured_image)}
                        alt={post.title}
                        className="w-full h-48 object-cover rounded-t-lg"
                        onError={hideBrokenImage}
                      />
                    )}
                    <div className="p-4">
                      <h3 className="font-bold text-lg mb-2 line-clamp-2">{post.title}</h3>
                      <p className="text-gray-600 text-sm line-clamp-2">{post.excerpt}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BlogDetail;
